import React from 'react';
import Cookies from 'js-cookie';
import ValidationHelper from './validation-helper';
import pageSpinner from '../page-spinner';
import throttleMessage from '../throttle-message';
import basketService from './basket-service';
import CurrencyHelper from './currency-helper';
import { PRODUCT_TAG_TICKET_PLUS, PRODUCT_TAG_TICKET_PLUS_EXTRA, PRODUCT_TYPE_TICKET } from './constants';

const TEST_LOCALSTORAGE_KEY = 'oAQNNl0Hd@mH';
const CHECKOUT_CONFIG_COOKIE = 'checkoutConfig';

export default class BasketController extends React.Component {
    constructor(props) {
        super(props);
        const basket = this.getPreparedBasket(basketService.getProductGroups(this.props.isConfiguredForBallot));
        this.state = {
            basket
        };

        this.isCheckoutInProgress = false;
        this.setCheckoutConfigCookies();
        this.onProductGroupsUpdate = this.onProductGroupsUpdate.bind(this);
        this.onUpdateProductQuantity = this.onUpdateProductQuantity.bind(this);
        this.onCheckout = this.onCheckout.bind(this);
        this.redirectToBasket = this.redirectToBasket.bind(this);
    }

    setCheckoutConfigCookies() {
        const urlParams = new URLSearchParams(window.location.search);

        if (!urlParams.has('checkoutConfig')) {
            return;
        }

        const checkoutConfigUid = urlParams.get('checkoutConfig');
        const regexCookieValidation = /(\.{2}\/|\.\/|\.{2}|\.|\/)/g;

        if (checkoutConfigUid.match(regexCookieValidation) !== null) {
            return;
        }

        Cookies.set(CHECKOUT_CONFIG_COOKIE, checkoutConfigUid, { secure: true });
    }

    onProductGroupsUpdate(productGroups, resetJunior = false) {
        this.setProductGroups(productGroups, resetJunior, true);
    }

    setProductGroups(productGroups, resetJunior, setToZero = false) {
        if (resetJunior) {
            ValidationHelper.resetJuniorProducts(productGroups, setToZero);
        }
        this.clearEmptyProductGroups(productGroups);
        basketService.saveProductGroups(productGroups, this.props.isConfiguredForBallot);
        const preparedBasket = this.getPreparedBasket(productGroups);
        this.setState({ basket: preparedBasket });
    }

    /**
     * Remove products which have zero quantity and remove empty product groups as well
     * @param productGroups
     */
    clearEmptyProductGroups(productGroups) {
        Object.values(productGroups).forEach((productGroup) => {
            productGroup.products = productGroup.products.filter((product) => product.quantity && product.quantity > 0);
            if (productGroup.products.length === 0) {
                delete productGroups[productGroup.id];
            }
        });
    }

    getPreparedBasket(productGroups) {
        let total = 0;
        let hasStandardTickets = false;

        Object.keys(productGroups).forEach((productGroupId) => {
            let basketProductSum = 0;
            // sort
            productGroups[productGroupId].products.sort((a, b) => a.sortOrder - b.sortOrder)
                .forEach((basketProduct) => {
                    const productSum = basketProduct.price * basketProduct.quantity;
                    // add sum to every product in basket
                    basketProduct.sum = productSum;
                    // calculate subtotal
                    basketProductSum += productSum;

                    // calculate total
                    total += productSum;

                    if (basketProduct.type === PRODUCT_TYPE_TICKET) {
                        hasStandardTickets = true;
                    }
                });
            productGroups[productGroupId].sum = basketProductSum;
        });

        const basket = {
            productGroups,
            total,
        };

        if (hasStandardTickets) {
            basket.totalMc = CurrencyHelper.getTotalMcFromTotal(total, this.props.masterCardDiscount);
        }

        return basket;
    }


    onUpdateProductQuantity(product, productGroup, quantity) {
        const basketProductGroups = this.state.basket.productGroups;

        // add product group to basket if it isn't there yet
        if (!basketProductGroups[productGroup.id]) {
            basketProductGroups[productGroup.id] = { ...productGroup };
            basketProductGroups[productGroup.id].products = [];
        }

        const basketProductGroup = basketProductGroups[productGroup.id];
        const basketProduct = basketProductGroup.products.find((bProduct) => bProduct.id === product.id);

        if (basketProduct) {
            if (basketProduct.tag === PRODUCT_TAG_TICKET_PLUS && quantity === 0) {
                const basketProductExtra = basketProductGroup.products.find((bProduct) => bProduct.tag === PRODUCT_TAG_TICKET_PLUS_EXTRA);

                if (basketProductExtra) {
                    basketProductExtra.quantity = quantity;
                }
            }

            basketProduct.quantity = quantity;
        } else {
            // add product with the passed quantity
            basketProductGroups[productGroup.id].products.push({
                ...product,
                quantity
            });
        }

        this.setProductGroups(basketProductGroups, ValidationHelper.isNeedResetJunior(product.type, product.tag));
    }

    onCheckout(mc = false) {
        throttleMessage.hide();

        if (!this.isCheckoutInProgress) {
            this.isCheckoutInProgress = true;
            pageSpinner.show();
            basketService.checkout(this.state.basket.productGroups, mc)
                .then(() => {
                    pageSpinner.hide();
                    this.isCheckoutInProgress = false;
                }, (error) => {
                    pageSpinner.hide();
                    if (error.message === '429') {
                        throttleMessage.show();
                    }
                    this.isCheckoutInProgress = false;
                }).catch(() => {
                    pageSpinner.hide();
                    this.isCheckoutInProgress = false;
                });
        }
    }

    redirectToBasket() {
        window.location = this.props.basketUrl;
    }

    isLocalStorageAvailable() {
        try {
            localStorage.setItem(TEST_LOCALSTORAGE_KEY, '1');
            localStorage.removeItem(TEST_LOCALSTORAGE_KEY);
            return true;
        } catch (error) {
            return false;
        }
    }


    render() {
        return (
            <React.Fragment>
                {
                    this.isLocalStorageAvailable() ?
                        this.props.render({
                            basket: this.state.basket,
                            onProductGroupsUpdate: this.onProductGroupsUpdate,
                            onUpdateProductQuantity: this.onUpdateProductQuantity,
                            onCheckout: this.props.redirectToBasketOnPay ? this.redirectToBasket : this.onCheckout
                        }) :
                        <div>
                            Something went wrong. Sorry for the inconvenience. In case you are using the private mode for your browser, please, try to switch it off.
                        </div>
                }
            </React.Fragment>
        );
    }
}
